import PropTypes from 'prop-types'
import { Avatar, AvatarGroup, Tooltip } from '@mui/material'
import { SelectableGroup, createSelectable } from 'react-selectable-fast'
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial'
import { statusColors } from '../theme'
import { cellId, dateKey, formatDayHeader, getProjectDayStaff } from '../utils'

const NAME_COL = 220
const ROW_H    = 56
const HEADER_H = 44

function colWidthFor(count) {
  if (count <= 7)  return 150
  if (count <= 14) return 120
  return 96
}

const SelectableProjectCell = createSelectable(function SelectableProjectCellInner({
  selectableRef, isSelecting, people, isWeekend, forceSelected, onToggle,
}) {
  const highlighted = isSelecting || forceSelected
  const inner =
    people.length === 0 ? (
      <div style={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 10, color: '#bbb' }}>—</span>
      </div>
    ) : (
      <Tooltip arrow title={people.map((p) => p.name).join(', ')}>
        <div style={{
          height: 40, borderRadius: 4, padding: '0 6px',
          background: statusColors.full.bg, color: statusColors.full.fg,
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 22, height: 22, fontSize: 9, borderColor: statusColors.full.bg } }}>
            {people.map((p) => (
              <Avatar key={p.id} sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff' }}>
                {p.initials}
              </Avatar>
            ))}
          </AvatarGroup>
          <span style={{ fontSize: 10, fontWeight: 700, marginLeft: 'auto' }}>{people.length}</span>
        </div>
      </Tooltip>
    )

  return (
    <td
      ref={selectableRef}
      className={[
        'schedule-td',
        highlighted ? 'selected-td' : '',
        isWeekend   ? 'is-weekend'  : '',
      ].filter(Boolean).join(' ')}
      style={{ padding: 6 }}
      onClick={onToggle}
    >
      {inner}
    </td>
  )
})
SelectableProjectCell.propTypes = {
  selectableKey: PropTypes.string.isRequired,
  people:        PropTypes.array.isRequired,
  isWeekend:     PropTypes.bool,
  forceSelected: PropTypes.bool,
  onToggle:      PropTypes.func,
}

export default function ProjectGrid({ projects, days, roster, scheduleMap, selection }) {
  const colWidth = colWidthFor(days.length)
  const { selected, groupRef, clearingKey, onSelectionFinish, toggleCell, selectRow, selectColumn } = selection

  return (
    <div className="schedule-grid" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

      {/* LEFT — frozen project name column */}
      <div style={{ flexShrink: 0, width: NAME_COL, display: 'flex', flexDirection: 'column', zIndex: 3 }}>
        <div
          className="col-frozen"
          style={{ height: HEADER_H, display: 'flex', alignItems: 'center', padding: '0 12px', flexShrink: 0 }}
        >
          <span style={{ fontSize: 11, fontWeight: 700, color: '#555', textTransform: 'uppercase' }}>
            Project
          </span>
        </div>
        {projects.map((p, row) => (
          <div
            key={p.id}
            className="row-frozen"
            style={{ height: ROW_H, display: 'flex', alignItems: 'center', padding: '0 8px', flexShrink: 0, cursor: 'pointer' }}
            onClick={() => selectRow(row)}
            title="Click to select whole row"
          >
            <div style={{
              width: 28, height: 28, borderRadius: 4,
              background: 'rgba(0,71,113,0.1)', color: 'var(--brand-color-1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <FolderSpecialIcon style={{ fontSize: 16 }} />
            </div>
            <span className="text-truncate ms-2" style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>
              {p.name}
            </span>
          </div>
        ))}
      </div>

      {/* RIGHT — scrollable data columns */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <div style={{ width: days.length * colWidth }}>

          <div style={{ display: 'flex', height: HEADER_H, position: 'sticky', top: 0, zIndex: 2 }}>
            {days.map((d, col) => {
              const f = formatDayHeader(d)
              return (
                <div
                  key={col}
                  className={`col-day-header${f.isToday ? ' is-today' : ''}${f.isWeekend ? ' is-weekend' : ''}`}
                  style={{ width: colWidth, flexShrink: 0 }}
                  onClick={() => selectColumn(col)}
                  title="Click to select whole column"
                >
                  <div style={{ fontSize: 11, fontWeight: 700, color: f.isToday ? 'var(--brand-color-2)' : '#333' }}>
                    {f.weekday}
                  </div>
                  <div style={{ fontSize: 10, color: '#666' }}>{f.dayMonth}</div>
                </div>
              )
            })}
          </div>

          <SelectableGroup
            key={clearingKey}
            ref={groupRef}
            onSelectionFinish={onSelectionFinish}
            enableDeselect
            tolerance={4}
            allowClickWithoutSelected
            resetOnStart={true}
            selectOnClick={false}
            component="div"
            style={{ position: 'relative' }}
          >
            <table style={{ tableLayout: 'fixed', borderCollapse: 'collapse', width: '100%' }}>
              <colgroup>
                {days.map((_, col) => <col key={col} style={{ width: colWidth }} />)}
              </colgroup>
              <tbody>
                {projects.map((p, row) => (
                  <tr key={p.id} style={{ display: 'table-row', height: ROW_H }}>
                    {days.map((d) => {
                      const key    = cellId(p.id, dateKey(d))
                      const people = getProjectDayStaff(scheduleMap, p.id, dateKey(d), roster)
                      const f      = formatDayHeader(d)
                      return (
                        <SelectableProjectCell
                          key={key}
                          selectableKey={key}
                          people={people}
                          isWeekend={f.isWeekend}
                          forceSelected={selected.has(key)}
                          onToggle={() => toggleCell(key)}
                        />
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </SelectableGroup>

        </div>
      </div>
    </div>
  )
}

ProjectGrid.propTypes = {
  projects:    PropTypes.array.isRequired,
  days:        PropTypes.array.isRequired,
  roster:      PropTypes.array.isRequired,
  scheduleMap: PropTypes.instanceOf(Map).isRequired,
  selection:   PropTypes.object.isRequired,
}
